/**
 * MODULE: _shared/lib/frontend/api/notificationApi.ts
 * PURPOSE: Tenant in-app notifications (data-management-service /api/v1/notifications) — G26 feed, snooze, mutes
 * DEPENDENCIES: axios, ./config (apiV1Url, getAuthHeaders)
 * PUBLIC API: notificationApi (list, markRead, snooze, preferences, unreadCount, …)
 * SIDE EFFECTS: HTTP with JWT from localStorage
 * AGENT_SAFETY: RESTRICTED
 * FILE_SIZE: 176 / 200 lines
 * RECENT: 2026-06-04 — **401** refresh+retry via **`axiosWithAuthRetry`**; read paths return silent fallback on **401** after refresh fails (parity with **429**). [2.2.874] **`isInGatewayBackoff`** …
 */

import axios from 'axios';
import {
  isBraznRefreshCooldownActive,
  refreshBraznAccessTokenOnce,
} from './authTokenRefresh';
import { apiV1Url, getAuthHeaders, getAuthToken } from './config';
import { bumpGateway429FromAxiosError, isInGatewayBackoff } from './gatewayRateLimit';

/** One Axios read/write; on **401**, refresh JWT once and retry with fresh **getAuthHeaders()**. */
async function axiosWithAuthRetry<T>(run: () => Promise<{ data: T }>): Promise<T> {
  try {
    const res = await run();
    return res.data;
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 401) {
      const refreshed = await refreshBraznAccessTokenOnce();
      if (refreshed) {
        const res = await run();
        return res.data;
      }
    }
    throw e;
  }
}

function notificationsUrl(rest: string): string {
  const r = rest === '/' ? '' : rest.startsWith('/') ? rest : `/${rest}`;
  return apiV1Url(`/notifications${r}`);
}

/**
 * One HTTP attempt; on 429 arm shared gateway cooldown and return fallback data (no retries — retries multiply console noise).
 * Skips the request entirely while cooldown is active so DevTools does not log another failed resource.
 */
async function executeNotificationReadData<D>(run: () => Promise<{ data: D }>, silentFallback: D): Promise<D> {
  if (isInGatewayBackoff() || isBraznRefreshCooldownActive() || !getAuthToken()) {
    return silentFallback;
  }
  try {
    return await axiosWithAuthRetry(run);
  } catch (e) {
    const st = axios.isAxiosError(e) ? e.response?.status : 0;
    if (st === 429) {
      bumpGateway429FromAxiosError(e);
      return silentFallback;
    }
    if (st === 401) {
      return silentFallback;
    }
    throw e;
  }
}

async function executeNotificationWrite<T>(run: () => Promise<{ data: T }>): Promise<T> {
  try {
    return await axiosWithAuthRetry(run);
  } catch (e) {
    const st = axios.isAxiosError(e) ? e.response?.status : 0;
    if (st === 429) {
      bumpGateway429FromAxiosError(e);
    }
    throw e;
  }
}

export type NotificationSeverity = 'info' | 'warning' | 'error' | 'success';
export type NotificationCategory = 'mention' | 'share_view' | 'deal_update' | 'system';

export interface TenantNotification {
  id: string;
  tenant_id: string;
  recipient_user_id: string;
  title: string;
  message: string;
  type: NotificationSeverity;
  category?: NotificationCategory;
  is_read?: boolean;
  snoozed_until?: string | null;
  action_url?: string;
  created_by?: string;
  created_at: string;
  updated_at?: string;
  status?: string;
  metadata?: Record<string, unknown>;
}

export interface NotificationPreferences {
  muted_categories: NotificationCategory[];
}

export const notificationApi = {
  list: async (params?: {
    category?: NotificationCategory;
    unread_only?: boolean;
    include_snoozed?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<TenantNotification[]> => {
    const data = await executeNotificationReadData(
      () =>
        axios.get<TenantNotification[]>(notificationsUrl('/'), {
          params,
          headers: getAuthHeaders(),
        }),
      [],
    );
    return Array.isArray(data) ? data : [];
  },

  get: async (notificationId: string): Promise<TenantNotification> => {
    return executeNotificationWrite(() =>
      axios.get<TenantNotification>(notificationsUrl(`/${notificationId}`), {
        headers: getAuthHeaders(),
      }),
    );
  },

  markRead: async (notificationId: string): Promise<TenantNotification> => {
    return executeNotificationWrite(() =>
      axios.post<TenantNotification>(
        notificationsUrl(`/${notificationId}/mark-read`),
        null,
        { headers: getAuthHeaders() },
      ),
    );
  },

  markAllRead: async (): Promise<{ marked_read: number }> => {
    return executeNotificationWrite(() =>
      axios.post<{ marked_read: number }>(
        notificationsUrl('/mark-all-read'),
        null,
        { headers: getAuthHeaders() },
      ),
    );
  },

  snooze: async (notificationId: string, hours = 24): Promise<void> => {
    await executeNotificationWrite(() =>
      axios.post(
        notificationsUrl(`/${encodeURIComponent(notificationId)}/snooze`),
        { hours },
        { headers: getAuthHeaders() },
      ),
    );
  },

  getPreferences: async (): Promise<NotificationPreferences> => {
    return executeNotificationReadData(
      () =>
        axios.get<NotificationPreferences>(notificationsUrl('/preferences'), {
          headers: getAuthHeaders(),
        }),
      { muted_categories: [] },
    );
  },

  putPreferences: async (body: NotificationPreferences): Promise<NotificationPreferences> => {
    return executeNotificationWrite(() =>
      axios.put<NotificationPreferences>(notificationsUrl('/preferences'), body, {
        headers: getAuthHeaders(),
      }),
    );
  },

  unreadCount: async (): Promise<number> => {
    const data = await executeNotificationReadData(
      () =>
        axios.get<{ count: number }>(notificationsUrl('/unread-count'), {
          headers: getAuthHeaders(),
        }),
      { count: 0 },
    );
    return typeof data?.count === 'number' ? data.count : 0;
  },

  delete: async (notificationId: string): Promise<void> => {
    await executeNotificationWrite(() =>
      axios.delete(notificationsUrl(`/${notificationId}`), {
        headers: getAuthHeaders(),
      }),
    );
  },
};
